<template>
  <div class="question-navigation-container">
    <!-- Fragenavigation -->
    <div class="question-navigation mb-2">
      <div class="nav-numbers">
        <button 
          v-for="(question, index) in questions" 
          :key="`nav_${index}`"
          class="nav-number"
          :class="[
            currentIndex === index ? 'active' : '',
            getAnswerStatus(question, index)
          ]"
          @click="!disableNavigation && navigateToQuestion(index)"
          :disabled="disableNavigation"
        >
          {{ index + 1 }}
        </button>
      </div>
    </div>
    
    <!-- Legende -->
    <div class="nav-legend">
      <div class="legend-item">
        <span class="legend-icon current"></span>
        <span class="legend-text">Aktuelle Frage</span>
      </div>
      <div class="legend-item">
        <span class="legend-icon answered"></span>
        <span class="legend-text">Beantwortet</span>
      </div>
      <div class="legend-item">
        <span class="legend-icon partial"></span>
        <span class="legend-text">Teilweise beantwortet</span>
      </div>
      <div class="legend-item">
        <span class="legend-icon unanswered"></span>
        <span class="legend-text">Nicht beantwortet</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'QuestionNavigationMenu',
  props: {
    questions: {
      type: Array,
      required: true
    },
    answers: {
      type: Array,
      required: true
    },
    currentIndex: {
      type: Number,
      required: true
    },
    disableNavigation: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    navigateToQuestion(index) {
      if (index >= 0 && index < this.questions.length) {
        this.$emit('navigate', index);
      }
    },
    getAnswerStatus(question, index) {
      const answer = this.answers[index];
      
      // Kein Wert = nicht beantwortet
      if (!answer) return 'unanswered';
      
      // Bei Textfragen: Jeder Text gilt als beantwortet
      if (question.type !== 'multiple_choice') {
        return answer.trim() !== '' ? 'answered' : 'unanswered';
      }
      
      // Bei Multiple-Choice prüfen, wie viele korrekte Antworten es gibt
      let correctOptionCount = 0;
      for (let i = 1; i <= 6; i++) {
        if (!!question[`correct_${i}`] || !!question[`is_answer_${i}_correct`]) {
          correctOptionCount++;
        }
      }
      
      // Wenn es nur eine richtige Antwort gibt und wir eine Option ausgewählt haben
      if (correctOptionCount <= 1 && Array.isArray(answer) && answer.length > 0) {
        return 'answered';
      }
      
      // Wenn es mehrere richtige Antworten gibt und wir einige, aber nicht alle ausgewählt haben
      if (correctOptionCount > 1 && Array.isArray(answer) && answer.length > 0) {
        if (answer.length < correctOptionCount) {
          return 'partial';
        } else {
          return 'answered';
        }
      }
      
      return 'unanswered';
    }
  }
};
</script>

<style lang="scss" scoped>
.question-navigation-container {
  margin-bottom: 1rem;
}

.question-navigation {
  display: flex;
  justify-content: center;
  overflow-x: auto;
  padding: 0.5rem 0;
  
  .nav-numbers {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: center;
  }
  
  .nav-number {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    border: 2px solid var(--border-color, #e5e7eb);
    background-color: var(--bg-color, white);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 0.8rem;
    cursor: pointer;
    transition: all 0.2s;
    
    &.active {
      background-color: var(--primary-color, #3b82f6);
      border-color: var(--primary-color, #3b82f6);
      color: white;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.25);
    }
    
    &.answered {
      border-color: var(--success-color, #10b981);
      border-width: 2px;
      color: var(--success-color, #10b981);
      
      &:not(.active):hover {
        background-color: rgba(16, 185, 129, 0.08);
      }
    }
    
    &.partial {
      border-color: var(--warning-color, #f59e0b);
      border-width: 2px;
      color: var(--warning-color, #f59e0b);
      
      &:not(.active):hover {
        background-color: rgba(245, 158, 11, 0.08);
      }
    }
    
    &.unanswered:not(.active):hover {
      background-color: var(--light-color, #f3f4f6);
    }
    
    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }
  }
}

.nav-legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
  padding: 0.75rem;
  background-color: var(--bg-light, #f9fafb);
  border-radius: 10px;
  margin-bottom: 0.5rem;
  font-size: 0.8rem;
  
  .legend-item {
    display: flex;
    align-items: center;
    
    .legend-icon {
      width: 16px;
      height: 16px;
      border-radius: 50%;
      margin-right: 0.4rem;
      border: 2px solid;
      
      &.current {
        background-color: var(--primary-color, #3b82f6);
        border-color: var(--primary-color, #3b82f6);
      }
      
      &.answered {
        background-color: white;
        border-color: var(--success-color, #10b981);
      }
      
      &.partial {
        background-color: white;
        border-color: var(--warning-color, #f59e0b);
      }
      
      &.unanswered {
        background-color: white;
        border-color: var(--border-color, #e5e7eb);
      }
    }
    
    .legend-text {
      color: var(--text-muted, #6b7280);
      font-weight: 500;
    }
  }
  
  .dark-mode & {
    background-color: var(--dark-bg-secondary, #1f2937);
    
    .legend-icon {
      &.answered, &.partial, &.unanswered {
        background-color: var(--dark-bg, #111827);
      }
    }
    
    .legend-text {
      color: var(--dark-text-muted, #9ca3af);
    }
  }
}

.dark-mode {
  .nav-number {
    background-color: var(--dark-bg, #111827);
    border-color: var(--dark-border-color, #374151);
    color: var(--dark-text-muted, #9ca3af);
    
    &:not(.active):not(.answered):not(.partial):hover {
      background-color: var(--dark-bg-hover, #2d3748);
    }
    
    &.answered:not(.active):hover {
      background-color: rgba(16, 185, 129, 0.15);
    }
    
    &.partial:not(.active):hover {
      background-color: rgba(245, 158, 11, 0.15);
    }
  }
}
</style> 